import { useParams } from 'react-router-dom';
import {
  IconButton,
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Skeleton,
  Link,
} from '@mui/material';
import { useGetAssetsQuery, useDeleteAssetMutation} from '../../services/api';
import { useDisableAssetMutation, useEnableAssetMutation } from '../../services/assetApi';
import type { Asset, PaymentConfig } from '../../services/api';
import { Add as AddIcon, Delete, History,PlayArrow, ToggleOff, ToggleOn } from '@mui/icons-material';
import EditIcon from '@mui/icons-material/Edit';
import { useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import AssetEdit from '../AssetEdit';
import { Link as RouterLink } from 'react-router-dom';
import { useAMC } from '@/context/AMCContext';

type TableAsset = Asset & {
  logoPath?: string;
  insertAotm?: boolean;
  insertDtr?: boolean;
  config?: PaymentConfig[];
  // dynamic routing fields coming from backend
  rules?: string | null;
  isUsingDynamicGatewayRouting?: boolean;
};

const AMCAssetsTab = () => {
  const { id = '' } = useParams<{ id: string }>();
  const { amcId } = useAMC();
  const { data: allAssets, isLoading } = useGetAssetsQuery(id);

  // Add state for show all toggle
  const [showAll, setShowAll] = useState(false);

  // Filter assets based on showAll toggle
  const assets = allAssets?.filter(asset => showAll || asset.isActive) || [];

  const [deleteAsset] = useDeleteAssetMutation();
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [disableAsset] = useDisableAssetMutation(); 
  const [enableAsset] = useEnableAssetMutation();   



  const [createOrEditDialogOpen, setCreateOrEditDialogOpen] = useState(false);
  const [chosenAssetId, setChosenAssetId] = useState<string | undefined>();

  const [actionType, setActionType] = useState<'disable' | 'enable'>('disable'); 
  const theme = useTheme();
  const showCrearteOrEditDialog = (assetId?: string) => {
    setChosenAssetId(assetId);
    setCreateOrEditDialogOpen(true);
  };
  const closeCreateOrEditDialog = () => {
    setCreateOrEditDialogOpen(false);
  };

  const handleDelete = async () => {
    try {
      if (actionType === 'disable') {
      await disableAsset({ id: chosenAssetId!, amcId: id }).unwrap();
      enqueueSnackbar('Asset disabled successfully', { variant: 'success' });
      } else {
        await enableAsset({id: chosenAssetId!, amcId: id}).unwrap();
        enqueueSnackbar('Asset enabled successfully', { variant: 'success' });
      }
      closeDeleteDialog();
    } catch (error: any) {
      enqueueSnackbar(error.data?.message || 'Failed to delete asset', {
        variant: 'error',
      });
    }
  };
  const showActionDialog = (assetId: string, actionType: 'disable' | 'enable') => {
    setChosenAssetId(assetId);
    setActionType(actionType);
    setActionDialogOpen(true);
  };
  const closeDeleteDialog = () => {
    setActionDialogOpen(false);
  };

  if (isLoading) {
    return (
      <Box sx={{ width: '100%', p: 2 }}>
        <TableContainer component={Paper} sx={{ borderRadius: '16px', mt: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                {[...Array(5)].map((_, i) => (
                  <TableCell key={i}>
                    <Skeleton variant="text" />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {[...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  {[...Array(5)].map((_, j) => (
                    <TableCell key={j}>
                      <Skeleton variant="text" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', px: 2 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            size="medium"
            startIcon={showAll ? <ToggleOn /> : <ToggleOff />}
            onClick={() => setShowAll(!showAll)}
            sx={{
              fontSize: '12px',
              borderRadius: '7px',
            }}
          >
            Show All
          </Button>
        </Box>

        <Button
          size="small"
          color="primary"
          disableElevation
          sx={{
            fontSize: '12px',
            borderRadius: '7px',
          }}
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => showCrearteOrEditDialog()}
        >
          Add Asset
        </Button>
      </Box>
      <TableContainer component={Paper} sx={{ borderRadius: '16px', mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ fontWeight: 'bold' }}>
              <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Insert Mandate</TableCell>
              <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Housekeeping</TableCell>
              <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Payment Configurations</TableCell>
              <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Dynamic routing</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {assets?.map((asset: TableAsset) => {
              const totalConfigs = asset.config?.length || 0;
              const configuredConfigs = asset.config?.filter(
                (config) =>
                  JSON.parse(((config as any).merchantConfig as unknown as string) || '[]')
                    .length || 0
              );
              const hasRules = !!asset.rules && `${asset.rules}`.trim() !== '' && `${asset.rules}` !== 'null';
              const noOfRules = hasRules ? JSON.parse(asset.rules || '{ rules: []}').rules.length : 0;
              const isDynamicEnabled = !!asset.isUsingDynamicGatewayRouting;
              return (
                <TableRow key={asset.id} hover sx={{
                  opacity: asset.isActive ? 1 : 0.5,
                  backgroundColor: asset.isActive ? 'inherit' : 'rgba(0, 0, 0, 0.04)'
                }} >
                  <TableCell>
                    <Link
                      component={RouterLink}
                      to={`/amcs/${amcId}/assets/${asset.id}`}
                      sx={{
                        color: theme.palette.primary.main,
                        fontSize: theme.typography.subtitle1.fontSize,
                        fontWeight: theme.typography.subtitle1.fontWeight,
                        textDecoration: 'none',
                      }}
                    >
                      <Box
                        sx={{
                          '&:hover': {
                            cursor: 'pointer',
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        {asset.name}
                      </Box>
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        display: 'inline-block',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1,
                        backgroundColor: asset.insertAotm
                          ? theme.palette.success.light
                          : theme.palette.error.light,
                        color: asset.insertAotm
                          ? theme.palette.success.main
                          : theme.palette.error.main,
                        fontWeight: 600,
                        textAlign: 'center',
                        width: '64px',
                      }}
                    >
                      {asset.insertAotm ? 'Yes' : 'No'}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        display: 'inline-block',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1,
                        backgroundColor: asset.insertDtr
                          ? theme.palette.success.light
                          : theme.palette.error.light,
                        color: asset.insertDtr
                          ? theme.palette.success.main
                          : theme.palette.error.main,
                        fontWeight: 600,
                        textAlign: 'center',
                        width: '64px',
                      }}
                    >
                      {asset.insertDtr ? 'Yes' : 'No'}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {isDynamicEnabled ? (
                      <Link
                        component={RouterLink}
                        to={`/amcs/${amcId}/assets/${asset.id}/try-dynamic-routing`}
                        sx={{
                          color: theme.palette.primary.main,
                          fontSize: theme.typography.subtitle1.fontSize,
                          fontWeight: theme.typography.subtitle1.fontWeight,
                          textDecoration: 'none',
                        }}
                      >
                        <Box
                          sx={{
                            '&:hover': {
                              cursor: 'pointer',
                              textDecoration: 'underline',
                            },
                          }}
                        >
                          {noOfRules} Dynamic rules configured
                        </Box>
                      </Link>
                    ) : (
                      <Link
                        component={RouterLink}
                        to={`/amcs/${amcId}/assets/${asset.id}/payment-configs`}
                        sx={{
                          color: theme.palette.primary.main,
                          fontSize: theme.typography.subtitle1.fontSize,
                          fontWeight: theme.typography.subtitle1.fontWeight,
                          textDecoration: 'none',
                        }}
                      >
                        <Box
                          sx={{
                            '&:hover': {
                              cursor: 'pointer',
                              textDecoration: 'underline',
                            },
                          }}
                        >
                          {configuredConfigs?.length || 0} of {totalConfigs} Static rules configured
                        </Box>
                      </Link>)}
                  </TableCell>
                  <TableCell>
                    <Link
                      component={RouterLink}
                      to={`/amcs/${amcId}/assets/${asset.id}/try-dynamic-routing`}
                      sx={{
                        color: theme.palette.primary.main,
                        fontSize: theme.typography.subtitle1.fontSize,
                        fontWeight: theme.typography.subtitle1.fontWeight,
                        textDecoration: 'none',
                      }}
                    >
                      <Box
                        sx={{
                          '&:hover': {
                            cursor: 'pointer',
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        {isDynamicEnabled ? (
                          '✅ Enabled'
                        ) : hasRules ? (
                          '💾 Saved'
                        ) : (
                          '🧪 Try Now'
                        )}
                      </Box>
                    </Link>
                  </TableCell>
                  <TableCell align="right">
                    <Link
                      component={RouterLink}
                      to={`/amcs/${amcId}/assets/${asset.id}/sample-transaction`}
                      state={{ 
                        data: isDynamicEnabled ? asset.rules : asset.config,
                        isDynamic : isDynamicEnabled 
                      }}
                      sx={{
                        color: theme.palette.primary.main,
                        fontSize: theme.typography.subtitle2.fontSize,
                        fontWeight: theme.typography.subtitle2.fontWeight,
                        textDecoration: 'none',
                        mr: 1.5,
                      }}    
                    >
                      <Box
                        sx={{
                          '&:hover': {
                            cursor: 'pointer',
                            textDecoration: 'underline',
                          },
                          display: 'inline-block',
                        }}
                      >
                        🚀 Sample Transaction
                      </Box>
                    </Link>
                    <IconButton
                      component={RouterLink}
                      to={`/amcs/${amcId}/assets/${asset.id}/history`}
                    >
                      <History fontSize="small" />
                    </IconButton>
                    <IconButton onClick={() => showCrearteOrEditDialog(asset.id)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    {asset.isActive ? (
                      <IconButton onClick={() => showActionDialog(asset.id, 'disable')}>
                        <Delete fontSize="small" color="error" />
                      </IconButton>
                    ) : (
                      <IconButton onClick={() => showActionDialog(asset.id, 'enable')}>
                        <PlayArrow fontSize="small" color="success" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={actionDialogOpen} onClose={closeDeleteDialog}>
        <DialogTitle>{actionType === 'disable' ? 'Disable' : 'Enable'} Asset</DialogTitle>
        <DialogContent>
          <DialogContentText>{actionType === 'disable' ? 'Are you sure you want to disable this asset?' : 'Are you sure you want to enable this asset?'}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog}>Cancel</Button>
          <Button onClick={handleDelete} color="error">
            {actionType === 'disable' ? 'Disable' : 'Enable'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={createOrEditDialogOpen}
        onClose={closeCreateOrEditDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          {!chosenAssetId ? 'Create Asset' : 'Edit Asset'}
        </DialogTitle>
        <DialogContent>
          <AssetEdit assetId={chosenAssetId!} onClose={closeCreateOrEditDialog} />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default AMCAssetsTab;
